import * as THREE from 'three';
import Experience from '../Experience.js';
import Data from '../Utils/Data.js';

export default class MeshUI {
    constructor() {
        this.experience = Experience.getInstance();
        this.camera = this.experience.camera.instance;
        this.scene = this.experience.scene;
        this.data = Data.getInstance();
        this.background = this.experience.world.background.imgMesh;
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();


        this.init();
        this.createButtons();
        this.initializeSliders();
        this.addEventListeners();
        this.startGame();
    }

    init() {
        this.buttonTextures = [
            '/textures/ui/heart.png',
            '/textures/ui/food.png',
            '/textures/ui/lighting.png'
        ];

        this.buttons = [];
        this.sliders = {};

        this.increaseStep = 5; //increase/decrease steps
        this.decreaseStep = 5;
    }

    createButtons() {
        const loader = new THREE.TextureLoader();
        const positionsX = [-0.47, 0.02, 0.51]; // X positions
        const positionsY = [-1.15, -1.26, -1.14]; // X positions


        for (let i = 0; i < this.buttonTextures.length; i++) {
            loader.load(this.buttonTextures[i], (texture) => {
                // Create geometry and material
                const geometry = new THREE.CircleGeometry(0.14, 32);
                const material = new THREE.MeshBasicMaterial({
                    map: texture,
                    transparent: false
                });

                // texture.encoding = THREE.sRGBEncoding;
                texture.colorSpace = THREE.SRGBColorSpace;

                // Create button mesh
                const button = new THREE.Mesh(geometry, material);
                button.position.set(positionsX[i], positionsY[i], 1);
                button.userData.originalColor = material.color.clone();
                button.userData.index = i;

                // Store and add to scene
                this.buttons.push(button);
                this.background.add(button);
            });
        }
    }

    addEventListeners() {
        window.addEventListener('pointerdown', this.onClick.bind(this));
        window.addEventListener('pointermove', this.onHover.bind(this));
    }

    initializeSliders() {
        // Attach event listeners to update indicators
        document.querySelectorAll(".slider-wrapper").forEach((wrapper) => {

            let slider = wrapper.querySelector("input[type='range']");
            let indicator = wrapper.querySelector(".indicator");

            slider.addEventListener("input", () => {
                updateIndicator(slider, indicator);
            });

            this.updateIndicator(slider, indicator); // Set initial position

            this.sliders[slider.id] = slider;
            slider.indicator = indicator;
        });
    }

    updateIndicator(slider, indicator) {
        let min = parseInt(slider.min);
        let max = parseInt(slider.max);
        let value = parseInt(slider.value);

        let percent = (value - min) / (max - min);  // Normalize value between 0 and 1
        let width = slider.clientWidth;  // Get slider width
        let offset = percent * width;  // Calculate position

        indicator.style.left = `${offset}px`;  // Move inside slider track
    }

    setSliderValue(sliderId, value) {
        let slider = this.sliders[sliderId];
        if (slider) {
            slider.value = value;
            this.updateIndicator(slider, slider.indicator);

            let event = new CustomEvent("sliderUpdated", {
                detail: {
                    id: sliderId
                }
            });
            document.dispatchEvent(event); // Dispatch event globally
        }
    }

    onClick(event) {
        if (!this.data.isCreatureActive) return;

        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.buttons);

        if (intersects.length > 0) {
            const button = intersects[0].object;

            switch (button.userData.index) {
                case 0: //love
                    this.data.creatureStats.love += this.increaseStep;
                    this.setSliderValue("love-slider", this.data.creatureStats.love);

                    break;
                case 1: //food
                    this.data.creatureStats.food += this.increaseStep;
                    this.setSliderValue("food-slider", this.data.creatureStats.food);

                    break;
                case 2: //activity
                    this.data.creatureStats.activity += this.increaseStep;
                    this.setSliderValue("activity-slider", this.data.creatureStats.activity);

                    break;
            }
        }
    }

    onHover(event) {
        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.buttons);

        this.buttons.forEach(button => {
            button.material.color.copy(button.userData.originalColor);
        });

        if (intersects.length > 0) {
            const button = intersects[0].object;
            button.material.color.set("#cacaca");
        }
    }

    startGame() {
        this.startGameTimeout = setTimeout(() => {
            this.decreaseLove();
            this.decreaseFood();
            this.decreaseActivity();
        }, 2000);
    }

    decreaseLove() {
        if (!this.data.isCreatureActive) return;

        this.loveTimeout = setTimeout(() => {
            this.data.creatureStats.love -= this.decreaseStep;
            this.setSliderValue("love-slider", this.data.creatureStats.love);

            this.decreaseLove();
        }, Math.random() * 5500 + 1000);
    }

    decreaseFood() {
        if (!this.data.isCreatureActive) return;

        this.foodTimeout = setTimeout(() => {
            this.data.creatureStats.food -= this.decreaseStep;
            this.setSliderValue("food-slider", this.data.creatureStats.food);

            this.decreaseFood();
        }, Math.random() * 5500 + 1000);
    }

    decreaseActivity() {
        if (!this.data.isCreatureActive) return;

        this.activityTimeout = setTimeout(() => {
            this.data.creatureStats.activity -= this.decreaseStep;
            this.setSliderValue("activity-slider", this.data.creatureStats.activity);

            this.decreaseActivity();
        }, Math.random() * 5500 + 1000);
    }

    blockEverything() {
        this.data.isCreatureActive = false;
        clearTimeout(this.loveTimeout);
        clearTimeout(this.foodTimeout);
        clearTimeout(this.activityTimeout);
    }

    reset() {
        this.data.creatureStats.love = 50;
        this.data.creatureStats.food = 50;
        this.data.creatureStats.activity = 50;
        this.setSliderValue("love-slider", 50);
        this.setSliderValue("food-slider", 50);
        this.setSliderValue("activity-slider", 50);
        this.data.isCreatureActive = true;
        this.startGame();
    }

    update() {

    }
}



// import * as THREE from 'three';
// import Experience from '../Experience.js';
// import * as ThreeMeshUI from 'three-mesh-ui';

// export default class MeshUI {
//     constructor() {
//         this.experience = Experience.getInstance();
//         this.scene = this.experience.scene;
//         this.background = this.experience.world.background.imgMesh;

//         this.buttonTextures = [
//             '/textures/ui/heart.png',
//             '/textures/ui/food.png',
//             '/textures/ui/lighting.png'
//         ];

//         this.buttonContainers = []; // Store individual button containers

//         this.createButtons();
//     }

//     createButtons() {
//         const loader = new THREE.TextureLoader();
//         const positions = [-0.4, 0, 0.4]; // X positions

//         for (let i = 0; i < this.buttonTextures.length; i++) {
//             loader.load(this.buttonTextures[i], (texture) => {
//                 // Create individual button container
//                 const buttonContainer = new ThreeMeshUI.Block({
//                     width: 0.24,
//                     height: 0.24,
//                     padding: 0.03,
//                     borderRadius: 0.12, // Rounded corners
//                     borderWidth: 0.005,
//                     borderColor: new THREE.Color("#00a5ab"),
//                     backgroundColor: new THREE.Color("#00dad7"),
//                 });

//                 // Create the button itself (inside its own container)
//                 const button = new ThreeMeshUI.Block({
//                     width: 0.16,
//                     height: 0.16,
//                     backgroundTexture: texture,
//                     borderRadius: 0,
//                     borderWidth: 0,
//                     // borderColor: new THREE.Color("#00a5ab"),
//                 });

//                 button.onPointerEnter = () => button.set({ borderColor: new THREE.Color(0x00ff00) });
//                 button.onPointerLeave = () => button.set({ borderColor: new THREE.Color(0xffffff) });

//                 buttonContainer.add(button);
//                 buttonContainer.position.set(positions[i], -0.8, 1);

//                 this.background.add(buttonContainer);
//                 this.buttonContainers.push(buttonContainer);

//                 buttonContainer.renderOrder = 10;
//             });
//         }
//     }

//     update() {
//         ThreeMeshUI.update();
//     }
// }
